import type { NamespaceTuple } from '@ownyou/shared-types';

export interface StoredMemoryRecord {
  key: string;
  namespace: string;
  userId: string;
  value: unknown;
  createdAt: number;
  updatedAt: number;
}

export interface BrowserQaStore {
  get<T>(namespace: NamespaceTuple, key: string): Promise<T | null>;
  put<T>(namespace: NamespaceTuple, key: string, value: T): Promise<void>;
  delete(namespace: NamespaceTuple, key: string): Promise<void>;
  list<T>(namespace: NamespaceTuple, options?: { limit?: number; offset?: number }): Promise<{ items: T[] }>;
}

function compositeKey(namespace: string, userId: string, key: string): string {
  return `${namespace}::${userId}::${key}`;
}

function memoryRecord(namespace: string, userId: string, key: string, value: unknown, existing?: StoredMemoryRecord): StoredMemoryRecord {
  const timestamp = Date.now();
  return {
    key: compositeKey(namespace, userId, key),
    namespace,
    userId,
    value,
    createdAt: existing?.createdAt ?? timestamp,
    updatedAt: timestamp,
  };
}

export async function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
}

export async function getDatabaseNames(): Promise<string[]> {
  const factory = indexedDB as IDBFactory & { databases?: () => Promise<Array<{ name?: string }>> };
  if (typeof factory.databases !== 'function') return [];
  const databases = await factory.databases();
  return databases.map((db) => db.name).filter(Boolean).filter((name) => name!.startsWith('ownyou_')) as string[];
}

async function getMemoryRecord(db: IDBDatabase, key: string): Promise<StoredMemoryRecord | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction('memories', 'readonly');
    const req = tx.objectStore('memories').get(key);
    req.onsuccess = () => resolve((req.result as StoredMemoryRecord | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function putMemoryRecord(db: IDBDatabase, record: StoredMemoryRecord): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction('memories', 'readwrite');
    tx.objectStore('memories').put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteMemoryRecord(db: IDBDatabase, key: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction('memories', 'readwrite');
    tx.objectStore('memories').delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getMemoryRecords(db: IDBDatabase): Promise<StoredMemoryRecord[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction('memories', 'readonly');
    const req = tx.objectStore('memories').getAll();
    req.onsuccess = () => resolve((req.result ?? []) as StoredMemoryRecord[]);
    req.onerror = () => reject(req.error);
  });
}

export function createBrowserQaStore(db: IDBDatabase): BrowserQaStore {
  return {
    async get<T>(namespace: NamespaceTuple, key: string): Promise<T | null> {
      const [ns, userId] = namespace;
      const record = await getMemoryRecord(db, compositeKey(ns, userId, key));
      return record ? (record.value as T) : null;
    },
    async put<T>(namespace: NamespaceTuple, key: string, value: T): Promise<void> {
      const [ns, userId] = namespace;
      const existing = await getMemoryRecord(db, compositeKey(ns, userId, key));
      await putMemoryRecord(db, memoryRecord(ns, userId, key, value, existing ?? undefined));
    },
    async delete(namespace: NamespaceTuple, key: string): Promise<void> {
      const [ns, userId] = namespace;
      await deleteMemoryRecord(db, compositeKey(ns, userId, key));
    },
    async list<T>(namespace: NamespaceTuple, options?: { limit?: number; offset?: number }): Promise<{ items: T[] }> {
      const [ns, userId] = namespace;
      const offset = options?.offset ?? 0;
      const limit = options?.limit ?? 100;
      const rows = await getMemoryRecords(db);
      return {
        items: rows
          .filter(row => row.namespace === ns && row.userId === userId)
          .slice(offset, offset + limit)
          .map(row => row.value as T),
      };
    },
  };
}

function backupKey(prefix: string, key: string): string {
  return `${prefix}${key}`;
}

export async function putWithBackup(
  store: BrowserQaStore,
  namespace: NamespaceTuple,
  key: string,
  value: unknown,
  backupPrefix: string,
): Promise<void> {
  const existing = await store.get(namespace, key);
  const existingBackup = await store.get(namespace, backupKey(backupPrefix, key));
  if (!existingBackup) {
    await store.put(namespace, backupKey(backupPrefix, key), {
      existed: existing !== null,
      value: existing,
      backedUpAt: Date.now(),
    });
  }
  await store.put(namespace, key, value);
}

export async function restoreBackup(
  store: BrowserQaStore,
  namespace: NamespaceTuple,
  key: string,
  backupPrefix: string,
): Promise<boolean> {
  const backup = await store.get<{ existed?: boolean; value?: unknown }>(namespace, backupKey(backupPrefix, key));
  if (!backup) return false;
  if (backup.existed) await store.put(namespace, key, backup.value);
  else await store.delete(namespace, key);
  await store.delete(namespace, backupKey(backupPrefix, key));
  return true;
}
